import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { Box, Button, Center, Divider, Group, Loader, MantineProvider, Stack, Tooltip, Transition } from '@mantine/core'
import * as Steps from '../../components/GlobalJobMatching/PricingVersionStepper/Steps'
import { SideProgress } from '../../components/GlobalJobMatching/PricingVersionStepper/SideProgress/SideProgress'
import { usePricingVersion, usePricingVersionRules, useRunTheRules } from '../../hooks/global-job-matching'
import { TitleBar } from '../../components/GlobalJobMatching/PricingVersionStepper/TitleBar/TitleBar'
import { useDisclosure } from '@mantine/hooks'
import { RuleSelectionModal } from '../../components/GlobalJobMatching/PricingVersionStepper/RuleSelectionModal'
import { globalJobMatchingTheme } from '../../lib/mantineTheme';
import { TopProgress } from '../../components/GlobalJobMatching/PricingVersionStepper/TopProgress'

const PricingVersionStepper = ({ launchDarklyFeatureFlags }: { launchDarklyFeatureFlags: Record<string, boolean> }) => {
  const params = useParams<{ id: string }>()
  const [stepIndex, setStepIndex] = useState(0)
  const [sideProgressOpened, setSideProgressOpened] = useState(false);
  const [ opened, { open, close }] = useDisclosure(false)
  const { data: version, isLoading: versionLoading, polling } = usePricingVersion(params?.id)
  const { data: rules, isLoading: rulesLoading } = usePricingVersionRules(params?.id)
  const runTheRulesMutation = useRunTheRules({ onMutate: () => setStepIndex(4)})
  const debugEnabled = launchDarklyFeatureFlags['glojam-debug']

  const steps = [
    { label: 'Define', color: '#6BBDD4', component: <Steps.Define debugEnabled={debugEnabled} /> },
    { label: 'Jobs', color: '#ED6629', component: <Steps.Jobs /> },
    { label: 'Data Cut', color: '#F1A628', component: <Steps.DataCuts /> },
    { label: 'Method', color: '#956FFA', component: <Steps.Methodology /> },
    { label: 'Review', color: '#44B369', component: <Steps.Review runTheRulesMutation={runTheRulesMutation} polling={polling} debugEnabled={debugEnabled} /> }
  ]

  const sideProgressWidth = 400

  const fileRequired = version?.matchStrategy === 'file' && !version?.metadata?.filename

  useEffect(() => {
    if (Array.isArray(rules)) open()
  }, [rules])

  if (versionLoading || rulesLoading) return (
    <Center mt='40vh'>
      <Loader size='xl' />
    </Center>
  )

  return (
    <MantineProvider theme={globalJobMatchingTheme}>
    <Stack gap={0} h='calc(100vh - 45px)' w='100%'>
      { Array.isArray(rules) && <RuleSelectionModal opened={opened} onClose={close} rules={rules} /> }
      <TitleBar runTheRulesMutation={runTheRulesMutation} polling={polling} stepIndex={stepIndex} />
      <Divider size='sm' mb={0} />
      <Group gap={0} wrap='nowrap' style={{ transition: 'margin-right 300ms ease', marginRight: sideProgressOpened ? sideProgressWidth : 0 }} align='flex-start'>
        <Stack w='100%'>
          <TopProgress
            steps={steps}
            fileRequired={fileRequired}
            sideProgressOpened={sideProgressOpened}
            stepIndex={stepIndex}
            setStepIndex={(idx) => setStepIndex(idx)}
            onOpen={() => setSideProgressOpened(true)}
          />
          <Divider size='sm' />
          <Box>
            { steps[stepIndex].component }
            <Group px='xl' justify='space-between' mt='lg' pos='sticky' bottom={5} w='100%'>
              { stepIndex === 0 ? <div></div> : <Button color='blueGrey' c='darkGrey' size='lg' disabled={stepIndex === 0} onClick={() => setStepIndex(prev => --prev)}>Back</Button> }
              { stepIndex !== 4 &&
                <Tooltip label='Must upload a match priority flile before continuing' disabled={!fileRequired}>
                  <Button size='lg' disabled={fileRequired} onClick={() => setStepIndex(prev => ++prev)}>Continue</Button>
                </Tooltip>
              }
            </Group>
          </Box>
        </Stack>
        <Transition
          mounted={sideProgressOpened}
          transition='slide-left'
          duration={300}
          timingFunction='ease'
        >
          {(transitionStyle) => (
            <SideProgress
              onClose={() => setSideProgressOpened(false)}
              pricingVersion={version}
              stepIndex={stepIndex}
              setStepIndex={setStepIndex}
              style={{
                ...transitionStyle, 
                position: 'fixed', 
                right: 0, 
                zIndex: 100, 
                width: sideProgressWidth, 
                borderLeft: '1px solid #e1e1e1',
                height: 'calc(100vh - 45px)',
                overflow: 'hidden'
              }}
            />
          )}
        </Transition>
      </Group>
    </Stack>
    </MantineProvider>
  )
}

export default PricingVersionStepper
